import { readText, writeText } from './storage';

/**
 * Procedural WebAudio SFX — zero assets, works offline, unlocks on the first
 * user gesture. Every cue varies pitch slightly so repeats never sound canned.
 */
const MUTE_KEY = 'chess3d.muted';

export class AudioSystem {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private muted = false;

  constructor(private readonly random: () => number) {
    this.muted = readText(MUTE_KEY) === '1';
  }

  get isMuted(): boolean {
    return this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master) this.master.gain.value = muted ? 0 : 0.8;
    writeText(MUTE_KEY, muted ? '1' : '0');
  }

  /** Call from a user gesture; safe to call repeatedly. */
  unlock(): void {
    if (this.context) {
      if (this.context.state === 'suspended') void this.context.resume();
      return;
    }
    try {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.gain.value = this.muted ? 0 : 0.8;
      this.master.connect(this.context.destination);
    } catch (error: unknown) {
      console.warn('WebAudio unavailable', error);
    }
  }

  /** Release the audio hardware. Browsers cap concurrent contexts, so a page that
   *  rebuilds its game (HMR, remount) must close the old one. */
  dispose(): void {
    const context = this.context;
    this.context = null;
    this.master = null;
    if (context && context.state !== 'closed') void context.close().catch(() => undefined);
  }

  private ready(): AudioContext | null {
    return this.context && this.context.state === 'running' && this.master ? this.context : null;
  }

  private vary(base: number, spread = 0.08): number {
    return base * (1 + (this.random() - 0.5) * 2 * spread);
  }

  private tone(frequency: number, duration: number, type: OscillatorType, gain: number, decay = true, detune = 0): void {
    const context = this.ready();
    if (!context || !this.master) return;
    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    oscillator.detune.value = detune;
    envelope.gain.setValueAtTime(gain, context.currentTime);
    if (decay) envelope.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + duration);
    oscillator.connect(envelope).connect(this.master);
    oscillator.start();
    oscillator.stop(context.currentTime + duration);
  }

  private noise(duration: number, gain: number, lowpass: number): void {
    const context = this.ready();
    if (!context || !this.master) return;
    const buffer = context.createBuffer(1, Math.ceil(context.sampleRate * duration), context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = (this.random() * 2 - 1) * (1 - i / data.length);
    const source = context.createBufferSource();
    source.buffer = buffer;
    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = lowpass;
    const envelope = context.createGain();
    envelope.gain.value = gain;
    source.connect(filter).connect(envelope).connect(this.master);
    source.start();
  }

  select(): void {
    this.tone(this.vary(880), 0.08, 'triangle', 0.12);
  }

  step(): void {
    this.noise(0.06, 0.18, 900);
    this.tone(this.vary(160, 0.2), 0.07, 'sine', 0.15);
  }

  hoof(): void {
    this.noise(0.05, 0.25, 500);
    this.tone(this.vary(120, 0.15), 0.06, 'sine', 0.2);
  }

  swing(): void {
    this.noise(0.14, 0.2, 2400);
  }

  hit(weight: number): void {
    this.noise(0.12 + weight * 0.12, 0.45, 1800);
    this.tone(this.vary(90, 0.1), 0.2 + weight * 0.15, 'square', 0.25 + weight * 0.2);
  }

  arrow(): void {
    this.noise(0.18, 0.18, 4000);
    this.tone(this.vary(1400), 0.12, 'sawtooth', 0.05);
  }

  spell(): void {
    for (let i = 0; i < 4; i += 1) {
      window.setTimeout(() => this.tone(this.vary(660 + i * 180, 0.03), 0.25, 'sine', 0.12), i * 45);
    }
    this.noise(0.4, 0.12, 3000);
  }

  poof(): void {
    this.noise(0.35, 0.22, 700);
  }

  check(): void {
    this.tone(523, 0.12, 'square', 0.14);
    window.setTimeout(() => this.tone(659, 0.16, 'square', 0.14), 130);
  }

  undo(): void {
    this.tone(500, 0.1, 'triangle', 0.12);
    window.setTimeout(() => this.tone(380, 0.14, 'triangle', 0.12), 90);
  }

  victory(): void {
    [523, 659, 784, 1047].forEach((frequency, index) => {
      window.setTimeout(() => this.tone(frequency, 0.35, 'triangle', 0.2), index * 140);
    });
  }

  defeat(): void {
    [440, 392, 330].forEach((frequency, index) => {
      window.setTimeout(() => this.tone(frequency, 0.4, 'sine', 0.18), index * 220);
    });
  }

  giggle(): void {
    [700, 900, 800, 1000].forEach((frequency, index) => {
      window.setTimeout(() => this.tone(this.vary(frequency), 0.09, 'triangle', 0.1), index * 70);
    });
  }
}
